import { PayloadAction } from '@reduxjs/toolkit'
import {
  INewGeneratedItemsInitialIdAndStatus,
  ITtsState
} from 'store/types/tts'
import {
  ITtsItem,
  ITtsLanguageOption,
  ITtsSpeaker,
  ITtsSpeakerTone,
  TTtsGenerationStatus
} from 'types/text-to-speech'
import {
  bulkUpdateTtsValuesByKey,
  removeDraftEmptyItems,
  setTtsItemStateByKey,
  setTtsItemValueByKey
} from './helpers'

const reducers = {
  setInitialTtsItems: (state: ITtsState, action: PayloadAction<ITtsItem[]>) => {
    state.ttsInitialItems = {
      data: action.payload,
      isLoading: false
    }
  },
  setInitialTtsItemsIsLoading: (
    state: ITtsState,
    action: PayloadAction<boolean>
  ) => {
    state.ttsInitialItems.isLoading = action.payload
  },
  setTtsItems: (state: ITtsState, action: PayloadAction<ITtsItem[]>) => {
    state.ttsMutableItems = {
      data: action.payload,
      isLoading: false
    }
  },
  setTtsItemsIsLoading: (state: ITtsState, action: PayloadAction<boolean>) => {
    state.ttsMutableItems.isLoading = action.payload
  },
  addNewTtsBlock: (state: ITtsState, action: PayloadAction<ITtsItem>) => {
    state.ttsMutableItems.data.push(action.payload)
  },
  setLanguageOptions: (
    state: ITtsState,
    action: PayloadAction<ITtsLanguageOption[]>
  ) => {
    state.languageOptions = {
      data: action.payload,
      isLoading: false
    }
  },
  setLanguageOptionsIsLoading: (
    state: ITtsState,
    action: PayloadAction<boolean>
  ) => {
    state.languageOptions.isLoading = action.payload
  },
  setSpeakersPerLangCode: (
    state: ITtsState,
    action: PayloadAction<{ langCode: string; speakers: ITtsSpeaker[] }>
  ) => {
    const { langCode, speakers } = action.payload
    state.speakersPerLangCode = {
      ...state.speakersPerLangCode,
      [langCode]: {
        data: speakers,
        isLoading: false
      }
    }
  },
  setSpeakersPerLangCodeIsLoading: (
    state: ITtsState,
    action: PayloadAction<{ langCode: string; isLoading: boolean }>
  ) => {
    const { langCode, isLoading } = action.payload
    if (!state.speakersPerLangCode) {
      state.speakersPerLangCode = {}
    }
    state.speakersPerLangCode[langCode] = {
      ...state.speakersPerLangCode[langCode],
      isLoading
    }
  },
  setSpeakerTones: (
    state: ITtsState,
    action: PayloadAction<ITtsSpeakerTone[]>
  ) => {
    state.speakerTones = {
      data: action.payload,
      isLoading: false
    }
  },
  setSpeakerTonesIsLoading: (
    state: ITtsState,
    action: PayloadAction<boolean>
  ) => {
    state.speakerTones.isLoading = action.payload
  },
  setTtsCredits: (state: ITtsState, action: PayloadAction<number>) => {
    state.ttsCredits = {
      data: action.payload,
      isLoading: false
    }
  },
  setTtsCreditsIsLoading: (
    state: ITtsState,
    action: PayloadAction<boolean>
  ) => {
    state.ttsCredits.isLoading = action.payload
  },
  updateTtsItemDataText: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: string
    }>
  ) => {
    const { id, value } = action.payload
    setTtsItemValueByKey({
      mutableTtsItems: state.ttsMutableItems.data,
      key: 'text',
      id,
      value
    })
  },
  updateTtsItemDataSpeakerId: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: number
    }>
  ) => {
    const { id, value } = action.payload
    setTtsItemValueByKey({
      mutableTtsItems: state.ttsMutableItems.data,
      key: 'speakerId',
      id,
      value
    })
  },
  updateTtsItemDataSpeed: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: number
    }>
  ) => {
    const { id, value } = action.payload
    setTtsItemValueByKey({
      mutableTtsItems: state.ttsMutableItems.data,
      key: 'speed',
      id,
      value
    })
  },
  updateTtsItemDataLangCode: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: string
    }>
  ) => {
    const { id, value } = action.payload
    setTtsItemValueByKey({
      mutableTtsItems: state.ttsMutableItems.data,
      key: 'langCode',
      id,
      value
    })
  },
  updateTtsItemDataInstruction: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: string
    }>
  ) => {
    const { id, value } = action.payload
    setTtsItemValueByKey({
      mutableTtsItems: state.ttsMutableItems.data,
      key: 'instructions',
      id,
      value
    })
  },
  bulkUpdateTtsItemsLangCode: (
    state: ITtsState,
    action: PayloadAction<{
      unchangedItems: number[]
      value: string
    }>
  ) => {
    const { unchangedItems, value } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems

    bulkUpdateTtsValuesByKey({
      mutableTtsItems,
      key: 'langCode',
      value,
      unchangedItems
    })
  },
  bulkUpdateTtsItemsSpeakerId: (
    state: ITtsState,
    action: PayloadAction<{
      unchangedItems: number[]
      value: number
    }>
  ) => {
    const { unchangedItems, value } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems

    bulkUpdateTtsValuesByKey({
      mutableTtsItems,
      key: 'speakerId',
      value,
      unchangedItems
    })
  },
  bulkUpdateTtsItemsSpeed: (
    state: ITtsState,
    action: PayloadAction<{
      unchangedItems: number[]
      value: number
    }>
  ) => {
    const { unchangedItems, value } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems

    bulkUpdateTtsValuesByKey({
      mutableTtsItems,
      key: 'speed',
      value,
      unchangedItems
    })
  },
  bulkUpdateTtsItemsInstruction: (
    state: ITtsState,
    action: PayloadAction<{
      unchangedItems: number[]
      value: string
    }>
  ) => {
    const { unchangedItems, value } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems

    bulkUpdateTtsValuesByKey({
      mutableTtsItems,
      key: 'instructions',
      value,
      unchangedItems
    })
  },
  updateTtsGeneratedState: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: boolean
    }>
  ) => {
    const { id, value } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems

    setTtsItemStateByKey({
      mutableTtsItems,
      stateKey: 'isGenerated',
      id,
      value
    })
  },
  updateTtsLoadingState: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: boolean
    }>
  ) => {
    const { id, value } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems

    setTtsItemStateByKey({
      mutableTtsItems,
      stateKey: 'isLoading',
      id,
      value
    })
  },
  deleteDraftTtsItem: (
    state: ITtsState,
    action: PayloadAction<{ id: number }>
  ) => {
    const { id } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems
    const itemIndex = mutableTtsItems.findIndex(item => item.data.id === id)

    if (itemIndex !== -1) {
      mutableTtsItems.splice(itemIndex, 1)
    }
  },
  deleteAllDraftEmptyTtsItems: (state: ITtsState) => {
    const { data: mutableTtsItems } = state.ttsMutableItems

    const filteredItems = removeDraftEmptyItems(mutableTtsItems)

    state.ttsMutableItems.data = filteredItems
  },
  setTtsGenerationStatus: (
    state: ITtsState,
    action: PayloadAction<TTtsGenerationStatus | null>
  ) => {
    state.ttsGenerationStatus = action.payload
  },
  setNewGeneratedItemsInitialIdAndStatus: (
    state: ITtsState,
    action: PayloadAction<INewGeneratedItemsInitialIdAndStatus[]>
  ) => {
    state.newGeneratedItemsInitialIdAndStatus = action.payload
  },
  setIsTtsMismatchPopupVisible: (
    state: ITtsState,
    action: PayloadAction<boolean>
  ) => {
    state.isTtsMismatchPopupVisible = action.payload
  }
}

export default reducers
